/**
 * V6 promo banner carousel — Bet365 style.
 *
 * Horizontal snap-scroll of full-width promo slides with prev/next
 * arrow controls on desktop and pagination dots beneath. Each slide
 * picks its own color theme (ochre / forest / royal / magenta /
 * ocean / ember) from the shared themes registry so the strip reads
 * as a vibrant offer carousel.
 *
 * Slide config + themes live in lib/v6/promoBanners.ts so the
 * /admin/banners page can override copy, icon, theme, and enabled-
 * state per row without forking this component.
 */
"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  Gift,
  Users,
  Trophy,
  Zap,
  Sparkles,
  Flame,
  Star,
  Rocket,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRegion } from "@/lib/regionContext";
import {
  DEFAULT_SLIDES,
  THEMES,
  loadPromoSlides,
  pickPromoStrings,
  slideMatchesRegion,
  type PromoIcon,
  type PromoSlideConfig,
  type PromoTheme,
} from "@/lib/v6/promoBanners";

const ICON_MAP: Record<PromoIcon, LucideIcon> = {
  gift: Gift,
  users: Users,
  trophy: Trophy,
  zap: Zap,
  sparkles: Sparkles,
  flame: Flame,
  star: Star,
  rocket: Rocket,
};

const AUTO_ADVANCE_MS = 6000;

export function V6PromoCarousel() {
  const railRef = useRef<HTMLDivElement | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [slides, setSlides] = useState<PromoSlideConfig[]>(DEFAULT_SLIDES);
  const [paused, setPaused] = useState(false);
  const { i18n } = useTranslation();
  const region = useRegion();

  // Hydrate from localStorage on mount + listen for admin edits so the
  // home rail reflects /admin/banners changes immediately.
  useEffect(() => {
    setSlides(loadPromoSlides());
    const refresh = () => setSlides(loadPromoSlides());
    window.addEventListener("granbay.promo-banners.changed", refresh);
    return () =>
      window.removeEventListener("granbay.promo-banners.changed", refresh);
  }, []);

  // Enabled + region-filtered. Banners with no `regions` array show
  // everywhere; banners with a specific list only show when the
  // visitor's region matches.
  const enabled = slides
    .filter((s) => s.enabled)
    .filter((s) => slideMatchesRegion(s, region.region));

  // Track which slide is most-visible so the pagination dots can light
  // up the right one as the rail snap-scrolls.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const handler = () => {
      const tiles = rail.querySelectorAll<HTMLDivElement>("[data-slide-index]");
      let bestIdx = 0;
      let bestDelta = Infinity;
      const railLeft = rail.getBoundingClientRect().left;
      tiles.forEach((el) => {
        const idx = Number(el.dataset.slideIndex ?? 0);
        const delta = Math.abs(el.getBoundingClientRect().left - railLeft);
        if (delta < bestDelta) {
          bestDelta = delta;
          bestIdx = idx;
        }
      });
      setActiveIdx(bestIdx);
    };
    rail.addEventListener("scroll", handler, { passive: true });
    return () => rail.removeEventListener("scroll", handler);
  }, [enabled.length]);

  function scrollToIdx(idx: number) {
    const rail = railRef.current;
    if (!rail) return;
    const slide = rail.querySelector<HTMLDivElement>(
      `[data-slide-index="${idx}"]`,
    );
    if (!slide) return;
    rail.scrollTo({
      left: slide.offsetLeft - rail.offsetLeft,
      behavior: "smooth",
    });
  }

  // Auto-advance every 6s. Pauses on hover (desktop) and on a manual
  // arrow click (resumes after 10s of inactivity). Wraps around to the
  // first slide when we reach the end.
  useEffect(() => {
    if (paused || enabled.length < 2) return;
    const id = window.setInterval(() => {
      setActiveIdx((idx) => {
        const next = (idx + 1) % enabled.length;
        const rail = railRef.current;
        if (rail) {
          const slide = rail.querySelector<HTMLDivElement>(
            `[data-slide-index="${next}"]`,
          );
          if (slide) {
            rail.scrollTo({
              left: slide.offsetLeft - rail.offsetLeft,
              behavior: "smooth",
            });
          }
        }
        return next;
      });
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [paused, enabled.length]);

  if (enabled.length === 0) return null;

  function manualPause() {
    setPaused(true);
    window.setTimeout(() => setPaused(false), 10000);
  }

  return (
    <section className="mx-auto mt-3 w-full max-w-[1280px] px-4">
      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Prev arrow — desktop only */}
        <button
          type="button"
          onClick={() => {
            manualPause();
            scrollToIdx(
              activeIdx === 0 ? enabled.length - 1 : activeIdx - 1,
            );
          }}
          aria-label="Previous promo"
          className="absolute left-2 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full transition-opacity hover:opacity-90 sm:flex"
          style={{
            background: "rgba(10, 10, 10, 0.75)",
            color: "#ededed",
            border: "1px solid rgba(255, 255, 255, 0.18)",
          }}
        >
          <ChevronLeft size={18} strokeWidth={2} aria-hidden="true" />
        </button>

        {/* Slides rail — single full-width slide on mobile;
            narrower + taller "poster" slides on desktop so 2-up
            peeks with the next slide hinted to the right
            (founder direction 2026-05-24). */}
        <div
          ref={railRef}
          className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 sm:gap-4"
          style={{ scrollbarWidth: "none", scrollPaddingInline: 0 }}
        >
          {enabled.map((slide, idx) => (
            <PromoSlide
              key={slide.id}
              slide={slide}
              index={idx}
              locale={i18n.language}
            />
          ))}
        </div>

        {/* Next arrow — desktop only */}
        <button
          type="button"
          onClick={() => {
            manualPause();
            scrollToIdx((activeIdx + 1) % enabled.length);
          }}
          aria-label="Next promo"
          className="absolute right-2 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full transition-opacity hover:opacity-90 sm:flex"
          style={{
            background: "rgba(10, 10, 10, 0.75)",
            color: "#ededed",
            border: "1px solid rgba(255, 255, 255, 0.18)",
          }}
        >
          <ChevronRight size={18} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {/* Pagination dots */}
      <div
        className="mt-2 flex items-center justify-center gap-1.5"
        role="tablist"
        aria-label="Promo slides"
      >
        {enabled.map((slide, idx) => (
          <button
            key={slide.id}
            type="button"
            role="tab"
            aria-selected={activeIdx === idx}
            aria-label={`Show ${slide.eyebrow}`}
            onClick={() => scrollToIdx(idx)}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: activeIdx === idx ? 20 : 6,
              background:
                activeIdx === idx
                  ? "var(--color-accent)"
                  : "var(--color-border2)",
            }}
          />
        ))}
      </div>
    </section>
  );
}

function PromoSlide({
  slide,
  index,
  locale,
}: {
  slide: PromoSlideConfig;
  index: number;
  locale: string;
}) {
  const Icon = ICON_MAP[slide.icon] ?? Gift;
  const theme: PromoTheme = THEMES[slide.theme] ?? THEMES.ochre;
  const t = pickPromoStrings(slide, locale);
  return (
    <div
      data-slide-index={index}
      className="w-full flex-shrink-0 snap-start sm:w-[55%] lg:w-[42%]"
    >
      {/* Mobile = horizontal row (icon · copy · CTA on one line at sm+
          internal, stacked at xs). Desktop slides switch to a poster
          shape: taller (220px+) and narrower (~42% of rail) so two
          slides peek and the rail feels like a Bet365 promo strip
          (founder direction 2026-05-24 — taller / narrower / auto-
          slide). */}
      <div
        className="relative flex flex-col items-stretch gap-3 overflow-hidden rounded-xl px-4 py-4 sm:gap-3 sm:px-5 sm:py-4"
        style={{
          background: theme.background,
          color: theme.textColor,
          border: `1px solid ${theme.borderColor}`,
          minHeight: 128,
        }}
      >
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 hidden h-full w-1 sm:block"
          style={{
            background: `linear-gradient(180deg, ${theme.accent} 0%, color-mix(in srgb, ${theme.accent} 60%, transparent) 100%)`,
          }}
        />
        <span
          aria-hidden="true"
          className="hidden sm:block"
          style={{
            position: "absolute",
            top: "-30%",
            right: "-15%",
            width: "55%",
            height: "160%",
            background: `radial-gradient(circle, ${theme.accent} 0%, transparent 60%)`,
            opacity: 0.18,
            pointerEvents: "none",
          }}
        />
        {/* Mobile-only horizontal layout (icon row + body + CTA). */}
        <div className="flex flex-1 items-center gap-3 sm:hidden">
          <div
            aria-hidden="true"
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[10px]"
            style={{ background: theme.iconBg, color: theme.iconColor }}
          >
            <Icon size={22} strokeWidth={2} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em]"
              style={{ color: theme.accent }}
            >
              {t.eyebrow}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-2">
              <span
                className="font-mono text-[22px] font-bold leading-none tabular-nums tracking-[-0.02em]"
                style={{ color: theme.amountColor }}
              >
                {t.amount}
              </span>
              <span
                className="text-[14px] font-semibold leading-tight"
                style={{ color: theme.textColor }}
              >
                {t.headline}
              </span>
            </div>
            <div
              className="mt-1 text-[11px] leading-[1.4]"
              style={{ color: theme.bodyColor }}
            >
              {t.body}
            </div>
          </div>
        </div>

        {/* Desktop-only poster layout (vertical stack).
         * Founder direction 2026-05-24 PM: 30% shorter than the
         * first poster pass — minHeight 200 → 140, icon tile
         * 56 → 44, amount 36 → 28px, gaps tightened so the rail
         * reads as a compact promo strip rather than a hero band. */}
        <div
          className="relative hidden flex-1 flex-col gap-2 sm:flex"
          style={{ minHeight: 140 }}
        >
          <div
            aria-hidden="true"
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[10px]"
            style={{ background: theme.iconBg, color: theme.iconColor }}
          >
            <Icon size={22} strokeWidth={2} aria-hidden="true" />
          </div>
          <div
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: theme.accent }}
          >
            {slide.eyebrow}
          </div>
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              className="font-mono text-[28px] font-bold leading-none tabular-nums tracking-[-0.03em]"
              style={{ color: theme.amountColor }}
            >
              {t.amount}
            </span>
            <span
              className="text-[14px] font-semibold leading-tight"
              style={{ color: theme.textColor }}
            >
              {t.headline}
            </span>
          </div>
          <div
            className="text-[11px] leading-[1.45]"
            style={{ color: theme.bodyColor }}
          >
            {t.body}
          </div>
        </div>

        <Link
          href={slide.ctaHref}
          className="inline-flex min-h-[44px] w-full flex-shrink-0 items-center justify-center whitespace-nowrap rounded-lg px-5 py-3 text-[13px] font-bold tracking-[0.01em] sm:w-auto sm:self-start"
          style={{ background: theme.accent, color: theme.iconColor }}
        >
          {t.ctaLabel}
        </Link>
      </div>
    </div>
  );
}
